'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState, useEffect } from 'react';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { getSlugFromFeature } from '@/lib/feature-slugs';
import { Card } from '@/components/ui/card';
import { Typography } from '@/components/ui/typography';
import * as LucideIcons from 'lucide-react';

type Feature = {
  id: string;
  title: string;
  description: string;
  icon?: string | null;
  category: string;
  hasDetailPage?: boolean;
  showOnHomePage?: boolean;
  slug?: string | null;
  locale?: string | null;
};

interface FeaturesGridProps {
  limit?: number;
  className?: string;
  showCategories?: boolean;
  showOnHomePageOnly?: boolean;
  /** SSG: pre-fetched features (skip client fetch when provided) */
  initialFeatures?: Feature[];
}

// Helper function to create slug from title
const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

export function FeaturesGrid({ limit, className, showCategories = false, showOnHomePageOnly = false, initialFeatures }: FeaturesGridProps) {
  const locale = useLocale();
  const t = useTranslations('features');
  const [features, setFeatures] = useState<Feature[]>(() => {
    if (initialFeatures?.length) {
      const filtered = initialFeatures.filter((f) => f && f.id && f.title);
      return limit ? filtered.slice(0, limit) : filtered;
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(!initialFeatures?.length);

  useEffect(() => {
    if (initialFeatures?.length) {
      const filtered = initialFeatures.filter((f) => f && f.id && f.title);
      setFeatures(limit ? filtered.slice(0, limit) : filtered);
      setIsLoading(false);
      return;
    }
    const fetchFeatures = async () => {
      try {
        setIsLoading(true);
        const url = showOnHomePageOnly
          ? `/api/features?locale=${locale}&showOnHomePage=true`
          : `/api/features?locale=${locale}`;
        const response = await fetch(url, {
          cache: 'no-store',
        });
        if (!response.ok) {
          setFeatures([]);
          return;
        }

        const data = await response.json();
        const fetchedFeatures = Array.isArray(data)
          ? data.filter((f: any) => f && f.id && f.title)
          : [];

        const finalFeatures = limit ? fetchedFeatures.slice(0, limit) : fetchedFeatures;
        setFeatures(finalFeatures);
      } catch (error) {
        setFeatures([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatures();
  }, [locale, limit, showOnHomePageOnly, initialFeatures?.length]);

  const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return null;
    const IconComponent = (LucideIcons as any)[iconName] as React.ComponentType<{ className?: string }> | undefined;
    return IconComponent;
  };

  // Group features by category
  const groupedFeatures = useMemo(() => {
    const groups: Record<string, Feature[]> = {};

    features
      .filter((feature) => feature && feature.id && feature.title)
      .forEach((feature) => {
        const category = feature.category || 'other';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(feature);
      });

    return groups;
  }, [features]);

  // Map features from API to card format
  const cards = useMemo(() => {
    return features
      .filter((feature) => feature && feature.id && feature.title)
      .map((feature) => {
        const Icon = getIconComponent(feature.icon);
        // Use slug from API if available (from English version), otherwise create from title
        const featureSlug = feature.slug || createSlug(feature.title);
        // Get translated slug if available, otherwise use the original slug
        const slug = getSlugFromFeature(featureSlug, locale) || featureSlug;
        return {
          id: feature.id || '',
          icon: Icon,
          title: feature.title || '',
          description: feature.description || '',
          category: feature.category || 'other',
          href: `${getLocalizedPagePath('mlm-software-features', locale)}/${slug}`,
          slug: slug,
        };
      });
  }, [features]);

  if (isLoading) {
    // Default to 3 columns for inner page, but allow override via className
    const defaultGridClass = className ? '' : 'md:grid-cols-3';
    return (
      <div className={`grid gap-6 ${defaultGridClass} ${className || ''}`}>
        {[...Array(limit || 6)].map((_, index) => (
          <div
            key={`skeleton-${index}`}
            className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className={`text-center py-12 ${className || ''}`}>
        <p className="text-gray-500 dark:text-gray-400 text-lg">
          {t('noFeaturesAvailable', { default: 'No features available yet.' })}
        </p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
          {t('featuresWillAppear', { default: 'Features will appear here once they are added through the admin panel.' })}
        </p>
      </div>
    );
  }

  // Default to 3 columns for inner page, but allow override via className
  const defaultGridClass = className ? '' : 'md:grid-cols-3';

  // If showing categories, group and display by category
  if (showCategories && Object.keys(groupedFeatures).length > 0) {
    const categoryOrder = ['general', 'security', 'performance', 'unique', 'other'];
    const sortedCategories = categoryOrder.filter(cat => groupedFeatures[cat]);

    // Add any other categories not in the order
    Object.keys(groupedFeatures).forEach(cat => {
      if (!categoryOrder.includes(cat)) {
        sortedCategories.push(cat);
      }
    });

    return (
      <div className={`space-y-12 ${className || ''}`}>
        {sortedCategories.map((category) => {
          const categoryFeatures = groupedFeatures[category];

          // Get translated category label
          const categoryLabel = t(`categories.${category}`, {
            default: category.charAt(0).toUpperCase() + category.slice(1) + ' Features'
          });

          return (
            <div key={category} className="space-y-6">
              <Typography variant="h2" className="!text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {categoryLabel}
              </Typography>
              <div className={`grid gap-6 ${defaultGridClass}`}>
                {categoryFeatures.map((feature, index) => {
                  const Icon = getIconComponent(feature.icon) || undefined;
                  // Use slug from API if available (from English version), otherwise try to identify the feature
                  // First, try to get the English identifier from the slug
                  let featureIdentifier = feature.slug || null;
                  
                  // If no slug, try to identify the feature by matching against known feature identifiers
                  // This handles cases where features don't have slugs stored
                  if (!featureIdentifier) {
                    // Try to match by checking if the title matches known feature titles
                    const titleLower = feature.title.toLowerCase();
                    if (titleLower.includes('bitcoin') && titleLower.includes('intégration')) {
                      featureIdentifier = 'bitcoin-integration';
                    } else if (titleLower.includes('sms') && titleLower.includes('intégration')) {
                      featureIdentifier = 'sms-integration';
                    } else if (titleLower.includes('pin') && (titleLower.includes('électronique') || titleLower.includes('electronique'))) {
                      featureIdentifier = 'e-pin';
                    } else if (titleLower.includes('portefeuille') || titleLower.includes('wallet')) {
                      featureIdentifier = 'e-wallet';
                    } else if (titleLower.includes('courrier') || titleLower.includes('email') || titleLower.includes('e-mail')) {
                      featureIdentifier = 'e-mail-system';
                    } else if (titleLower.includes('ticket') || titleLower.includes('support') || titleLower.includes('soporte') || titleLower.includes('soutien') || titleLower.includes('supporto')) {
                      featureIdentifier = 'ticket-system';
                    } else if (titleLower.includes('voucher') || titleLower.includes('cupon') || titleLower.includes('bon') || titleLower.includes('buono') || titleLower.includes('gutschein')) {
                      featureIdentifier = 'e-voucher';
                    } else if (titleLower.includes('replicating') || titleLower.includes('replicador') || titleLower.includes('répliquant') || titleLower.includes('replicante') || titleLower.includes('replizierend')) {
                      featureIdentifier = 'replicating-website';
                    } else if (titleLower.includes('genealogy') || titleLower.includes('genealogia') || titleLower.includes('généalogie') || titleLower.includes('genealogia') || titleLower.includes('stammbaum') || titleLower.includes('tree') || titleLower.includes('arbol') || titleLower.includes('arbre') || titleLower.includes('albero')) {
                      featureIdentifier = 'genealogy-tree';
                    }
                  }
                  
                  // Get translated slug for the locale
                  const slug = featureIdentifier 
                    ? (getSlugFromFeature(featureIdentifier, locale) || featureIdentifier)
                    : (createSlug(feature.title));
                  // Get translated page path
                  const { getSlugFromPage } = require('@/lib/page-slugs');
                  const pagePath = getSlugFromPage('mlm-software-features', locale) || 'mlm-software-features';
                  const href = getLocalizedPath(`${pagePath}/${slug}`, locale);

                  // Use hasDetailPage from the feature data (inherited from English)
                  const hasDetailPage = feature.hasDetailPage ?? false;

                  return (
                    <Card
                      key={feature.id}
                      title={feature.title}
                      description={feature.description}
                      href={hasDetailPage ? href : undefined}
                      icon={Icon}
                      index={index}
                      showIndex={false}
                      showFooterLink={hasDetailPage}
                      footerLinkText={t('exploreMore', { default: 'Explore More' })}
                      animationDelay={index * 80}
                      titleVariant="h4"
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Default grid display (for home page)
  return (
    <div className={`grid gap-6 ${defaultGridClass} ${className || ''}`}>
      {cards.map((card, index) => {
        // Use slug from card (which comes from API, inherited from English)
        let featureIdentifier = card.slug || null;
        
        // If no slug, try to identify the feature by matching against known feature identifiers
        if (!featureIdentifier) {
          const titleLower = card.title.toLowerCase();
          if (titleLower.includes('bitcoin') && (titleLower.includes('intégration') || titleLower.includes('integration'))) {
            featureIdentifier = 'bitcoin-integration';
          } else if (titleLower.includes('sms') && (titleLower.includes('intégration') || titleLower.includes('integration'))) {
            featureIdentifier = 'sms-integration';
          } else if (titleLower.includes('pin') && (titleLower.includes('électronique') || titleLower.includes('electronique') || titleLower.includes('electronic'))) {
            featureIdentifier = 'e-pin';
          } else if (titleLower.includes('portefeuille') || titleLower.includes('wallet') || titleLower.includes('billetera')) {
            featureIdentifier = 'e-wallet';
          } else if (titleLower.includes('courrier') || titleLower.includes('email') || titleLower.includes('e-mail') || titleLower.includes('correo')) {
            featureIdentifier = 'e-mail-system';
          } else if (titleLower.includes('ticket') || titleLower.includes('support') || titleLower.includes('soporte') || titleLower.includes('soutien') || titleLower.includes('supporto') || titleLower.includes('biglietti')) {
            featureIdentifier = 'ticket-system';
          } else if (titleLower.includes('voucher') || titleLower.includes('cupon') || titleLower.includes('bon') || titleLower.includes('buono') || titleLower.includes('gutschein')) {
            featureIdentifier = 'e-voucher';
          } else if (titleLower.includes('replicating') || titleLower.includes('replicador') || titleLower.includes('répliquant') || titleLower.includes('replicante') || titleLower.includes('replizierend')) {
            featureIdentifier = 'replicating-website';
          } else if (titleLower.includes('genealogy') || titleLower.includes('genealogia') || titleLower.includes('généalogie') || titleLower.includes('genealogia') || titleLower.includes('stammbaum') || titleLower.includes('tree') || titleLower.includes('arbol') || titleLower.includes('arbre') || titleLower.includes('albero')) {
            featureIdentifier = 'genealogy-tree';
          }
        }
        
        // Get translated slug for the locale
        const slug = featureIdentifier 
          ? (getSlugFromFeature(featureIdentifier, locale) || featureIdentifier)
          : (createSlug(card.title));
        // Get translated page path
        const { getSlugFromPage } = require('@/lib/page-slugs');
        const pagePath = getSlugFromPage('mlm-software-features', locale) || 'mlm-software-features';
        const href = getLocalizedPath(`${pagePath}/${slug}`, locale);
        const Icon = card.icon || undefined;

        // Find the feature to check if it has a detail page (inherited from English)
        const feature = features.find(f => f.id === card.id);
        const hasDetailPage = feature?.hasDetailPage ?? false;

        return (
          <Card
            key={card.id}
            title={card.title}
            description={card.description}
            href={hasDetailPage ? href : undefined}
            icon={Icon}
            index={index}
            showIndex={false}
            showFooterLink={hasDetailPage}
            footerLinkText={t('exploreMore', { default: 'Explore More' })}
            animationDelay={index * 80}
            titleVariant="h4"
          />
        );
      })}
    </div>
  );
}

